import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MoreOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SyncOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { servicesService } from '../../../services';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';

export interface CardActionMenuProps {
  /** ID del servicio para las acciones (start/stop/restart/delete) */
  serviceId: string;
  /** Estado normalizado de la tarjeta */
  status: 'running' | 'stopped' | 'deploying' | 'error' | string;
  /** Estado real del backend (para detectar deleting/deleted) */
  realStatus?: string;
  /** Ruta a la que navega "Ver detalles" */
  detailRoute: string;
  /** Nombre del servicio (para el modal de confirmación) */
  serviceName?: string;
  /** Refresca la lista tras una acción */
  onRefresh?: () => void;
  /** Muestra Iniciar/Detener/Reiniciar (default true). Deploy Apps multi-app => false */
  supportsLifecycle?: boolean;
  /** Muestra Eliminar (default true) */
  canDelete?: boolean;
}

/**
 * Menú "···" homologado para las tarjetas de listas (VM, DB, marketplace, etc.).
 * Empaqueta el dropdown, las acciones de ciclo de vida y el modal de borrado.
 * Frena la propagación para no disparar el onClick de la tarjeta contenedora.
 */
const CardActionMenu: React.FC<CardActionMenuProps> = ({
  serviceId,
  status,
  realStatus,
  detailRoute,
  serviceName,
  onRefresh,
  supportsLifecycle = true,
  canDelete = true,
}) => {
  const navigate = useNavigate();
  const { success, error } = useNotificationContext();
  const [open, setOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const normalized = (status || '').toLowerCase();
  const real = (realStatus || '').toLowerCase();
  const isRunning = normalized === 'running';
  const isStopped = normalized === 'stopped';
  const isBusy = real === 'deleting' || real === 'deleted' || normalized === 'deploying';

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const itemCls = (disabled: boolean) =>
    `w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors ${
      disabled
        ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
    }`;

  const runAction = async (e: React.MouseEvent, action: 'start' | 'stop' | 'restart') => {
    e.stopPropagation();
    setOpen(false);
    if (!serviceId) return;
    setIsProcessing(true);
    try {
      if (action === 'start') await servicesService.start(serviceId);
      else if (action === 'stop') await servicesService.stop(serviceId);
      else await servicesService.restart(serviceId);
      success(
        action === 'start'
          ? 'Servicio iniciado'
          : action === 'stop'
          ? 'Servicio detenido'
          : 'Servicio reiniciado'
      );
      if (onRefresh) setTimeout(() => onRefresh(), 1000);
    } catch (err: any) {
      error(err?.message || 'Ocurrió un error al ejecutar la acción');
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmDelete = async () => {
    if (!serviceId) return;
    setDeleting(true);
    try {
      await servicesService.delete(serviceId);
      success('Servicio eliminado');
      setShowDelete(false);
      if (onRefresh) setTimeout(() => onRefresh(), 800);
    } catch (err: any) {
      error(err?.message || 'No se pudo eliminar el servicio');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div ref={ref} className="relative" onClick={stop}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        aria-label="Acciones"
      >
        <MoreOutlined />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div
            className="absolute right-0 mt-1 w-48 bg-white dark:bg-dark-bg rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
            onClick={stop}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                navigate(detailRoute);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3"
            >
              <EyeOutlined />
              Ver detalles
            </button>

            {supportsLifecycle && isRunning && (
              <>
                <button
                  type="button"
                  onClick={(e) => runAction(e, 'restart')}
                  disabled={isProcessing || isBusy}
                  className={itemCls(isProcessing || isBusy)}
                >
                  {isProcessing ? <LoadingOutlined spin /> : <SyncOutlined />}
                  Reiniciar
                </button>
                <button
                  type="button"
                  onClick={(e) => runAction(e, 'stop')}
                  disabled={isProcessing || isBusy}
                  className={itemCls(isProcessing || isBusy)}
                >
                  {isProcessing ? <LoadingOutlined spin /> : <PauseCircleOutlined />}
                  Detener
                </button>
              </>
            )}
            {supportsLifecycle && isStopped && (
              <button
                type="button"
                onClick={(e) => runAction(e, 'start')}
                disabled={isProcessing || isBusy}
                className={itemCls(isProcessing || isBusy)}
              >
                {isProcessing ? <LoadingOutlined spin /> : <PlayCircleOutlined />}
                Iniciar
              </button>
            )}

            {canDelete && (
              <>
                <hr className="my-1 border-gray-200 dark:border-gray-700" />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    setShowDelete(true);
                  }}
                  disabled={isBusy}
                  className="w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <DeleteOutlined />
                  Eliminar
                </button>
              </>
            )}
          </div>
        </>
      )}

      <ConfirmationModal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={confirmDelete}
        title="Eliminar servicio"
        message={`¿Seguro que deseas eliminar${serviceName ? ` "${serviceName}"` : ' este servicio'}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
        loading={deleting}
      />
    </div>
  );
};

export default CardActionMenu;
